import { Injectable } from "@angular/core";
import {
	Basics,
	Member,
	RestService,
	SessionService,
	ComponentFormat,
	UiComponent,
	UiComponentService,
	UiSearchService,
} from "@recore-solutions/nstar-lib";
import * as merge from "deepmerge";
import { combineLatest, Observable } from "rxjs";
import { filter, map, shareReplay, take, tap } from "rxjs/operators";
import packageInfo from "../../package.json";
import { PreferencesService } from "./preferences/preferences.service";
import { Configuration } from "./shared/interfaces/configuration";
import { LoonFnFactoriesService } from "./shared/services/loon-fn-factories.service";
import { MemberFnFactoriesService } from "./shared/services/member-fn-factories.service";
import { OfficeMemberFnFactoriesService } from "./shared/services/office-member-fn-factories.service";
import { PostalCityFnFactoriesService } from "./shared/services/postal-city-fn-factories.service";
import { SchoolDistrictFnFactoriesService } from "./shared/services/school-district-fn-factories.service";
import { SchoolFnFactoriesService } from "./shared/services/school-fn-factories.service";
import { TeamMemberFnFactoriesService } from "./shared/services/team-member-fn-factories.service";
import { OfficeFnFactoriesService } from "./shared/services/office-fn-factories.service";
import { BuilderFnFactoriesService } from "./shared/services/builder-fn-factories.service";
import { ListingCityFnFactoriesService } from "./shared/services/listing-city-fn-factories.service";
import { CountyFnFactoriesService } from "./shared/services/county-fn-factories.service";

@Injectable({
	providedIn: "root",
})
export class ConfigurationService {
	config$: Observable<Configuration>;
	config: Configuration;

	constructor(
		private restService: RestService,
		private sessionService: SessionService,
		private preferencesService: PreferencesService,
		private uiSearchService: UiSearchService,
		private loonFnFactoriesService: LoonFnFactoriesService,
		private schoolFnFactoriesService: SchoolFnFactoriesService,
		private schoolDistrictFnFactoriesService: SchoolDistrictFnFactoriesService,
		private postalCityFnFactoriesService: PostalCityFnFactoriesService,
		private teamMemberFnFactoriesService: TeamMemberFnFactoriesService,
		private officeMemberFnFactoriesService: OfficeMemberFnFactoriesService,
		private uiComponentService: UiComponentService,
		private memberFnFactoriesService: MemberFnFactoriesService,
		private officFnFactoriesService: OfficeFnFactoriesService,
		private builderFnFactoriesService: BuilderFnFactoriesService,
		private listingCityFnFactoriesService: ListingCityFnFactoriesService,
		private countyFnFactoriesService: CountyFnFactoriesService
	) {}

	initializeConfiguration(): Observable<Configuration> {
		this.config$ = combineLatest([
			this.restService.get("./assets/config.json?v=" + packageInfo.version),
			this.sessionService.memberBasic$.pipe(filter((member) => !!member)),
			this.preferencesService.preference$,
		]).pipe(
			map(this.combineConfigurations),
			tap((config) => (this.config = config)),
			shareReplay({ bufferSize: 1, refCount: true })
		);
		return this.config$.pipe(take(1));
	}

	registerSearchServiceFactories(): void {
		this.uiSearchService.fnFactories = this.loonFnFactoriesService.factories;
		this.uiSearchService.tableFnFactories.Office = this.officFnFactoriesService.factories;
		this.uiSearchService.tableFnFactories.OfficeMember =
			this.officeMemberFnFactoriesService.factories;
		this.uiSearchService.tableFnFactories.Member = this.memberFnFactoriesService.factories;
		this.uiSearchService.tableFnFactories.School = this.schoolFnFactoriesService.factories;
		this.uiSearchService.tableFnFactories.TeamMember = this.teamMemberFnFactoriesService.factories;
		this.uiSearchService.tableFnFactories.Builder = this.builderFnFactoriesService.factories;
		this.uiSearchService.tableFnFactories.ListingCity =
			this.listingCityFnFactoriesService.factories;
		this.uiSearchService.tableFnFactories.County = this.countyFnFactoriesService.factories;
		this.uiSearchService.columnFnFactories = {
			City: {
				citySK: this.postalCityFnFactoriesService.factories,
			},
		};
		this.uiSearchService.tableFnFactories.SchoolDistrict =
			this.schoolDistrictFnFactoriesService.factories;
	}

	registerForeignKeys(uiComponent: UiComponent<any>[]): void {
		const uniqueTables = uiComponent.filter(
			(u, i) =>
				!!u.databaseTable &&
				i === uiComponent.findIndex((uu) => uu.databaseTable === u.databaseTable)
		);
		const tables = uniqueTables.map((u) => u.databaseTable);
		const featurePrefixes = [
			"Listing",
			"SingleFamily",
			"Multifamily",
			"Farm",
			"Land",
			"Commercial",
			"Rental",
			"ParcelNumber",
		];
		const feature = tables.filter(
			(t) => !!t && t.indexOf("Code") === -1 && featurePrefixes.find((p) => t.indexOf(p) === 0)
		);
		const listingFeature = tables.filter((t) => !!t && t.indexOf("Feature") === 0);
		const outbuilding = ["OutbuildingFloor", "OutbuildingConstruction"];
		feature.forEach((f) => {
			this.uiComponentService.registerForeignKeyMapping(
				{
					databaseColumn: "Listing_SK",
					databaseTable: f,
					applicationPropertyName: "listingSK",
					databaseName: null,
					schemaName: null,
					keyType: null,
					componentDataType: null,
				} as ComponentFormat,
				{
					databaseColumn: "Listing_SK",
					databaseTable: "Listing",
					applicationPropertyName: "listingSK",
					databaseName: null,
					schemaName: null,
					keyType: null,
					componentDataType: null,
				} as ComponentFormat
			);
		});
		listingFeature.forEach((f) => {
			this.uiComponentService.registerForeignKeyMapping(
				{
					databaseColumn: "ListingFeature_SK",
					databaseTable: f,
					applicationPropertyName: "listingFeatureSK",
					databaseName: null,
					schemaName: null,
					keyType: null,
					componentDataType: null,
				} as ComponentFormat,
				{
					databaseColumn: "Listing_SK",
					databaseTable: "Listing",
					applicationPropertyName: "listingSK",
					databaseName: null,
					schemaName: null,
					keyType: null,
					componentDataType: null,
				} as ComponentFormat
			);
		});
		outbuilding.forEach((o) => {
			this.uiComponentService.registerForeignKeyMapping(
				{
					databaseColumn: "FeatureOutbuilding_SK",
					databaseTable: o,
					applicationPropertyName: "featureOutbuildingSK",
					databaseName: null,
					schemaName: null,
					keyType: null,
					componentDataType: null,
				} as ComponentFormat,
				{
					databaseColumn: "FeatureOutbuilding_SK",
					databaseTable: "FeatureOutbuilding",
					applicationPropertyName: "featureOutbuildingSK",
					databaseName: null,
					schemaName: null,
					keyType: null,
					componentDataType: null,
				} as ComponentFormat
			);
		});
		const multifamilyUnitTables: string[] = [
			"MultifamilyUnitRoomType",
			"MultifamilyUnitFireplace",
			"MultifamilyUnitBath",
			"MultifamilyUnitAppliance",
			"MultifamilyUnitCooling",
			"MultifamilyUnitDiningRoom",
			"MultifamilyUnitAmenity",
			"MultifamilyUnitFamilyRoom",
			"MultifamilyUnitSpecialSearch",
		];
		multifamilyUnitTables.forEach((databaseTable) => {
			this.uiComponentService.registerForeignKeyMapping(
				{
					databaseColumn: "MultifamilyUnit_SK",
					databaseTable,
					applicationPropertyName: "multifamilyUnitSK",
					databaseName: null,
					schemaName: null,
					keyType: null,
					componentDataType: null,
				} as ComponentFormat,
				{
					databaseColumn: "MultifamilyUnit_SK",
					databaseTable: "MultifamilyUnit",
					applicationPropertyName: "multifamilyUnitSK",
					databaseName: null,
					schemaName: null,
					keyType: null,
					componentDataType: null,
				} as ComponentFormat
			);
		});
		this.uiComponentService.registerForeignKeyMapping(
			{
				databaseColumn: "MultifamilyUnit_SK",
				databaseTable: "MultifamilyUnitRoomType",
				applicationPropertyName: "multifamilyUnitSK",
				databaseName: null,
				schemaName: null,
				keyType: null,
				componentDataType: null,
			} as ComponentFormat,
			{
				databaseColumn: "MultifamilyUnit_SK",
				databaseTable: "MultifamilyUnit",
				applicationPropertyName: "multifamilyUnitSK",
				databaseName: null,
				schemaName: null,
				keyType: null,
				componentDataType: null,
			} as ComponentFormat
		);
		this.uiComponentService.registerForeignKeyMapping(
			{
				databaseColumn: "ListingFeature_SK",
				databaseTable: "FeatureWaterFeature",
				applicationPropertyName: "listingFeatureSK",
				databaseName: null,
				schemaName: null,
				keyType: null,
				componentDataType: null,
			} as ComponentFormat,
			{
				databaseColumn: "Listing_SK",
				databaseTable: "Listing",
				applicationPropertyName: "listingSK",
				databaseName: null,
				schemaName: null,
				keyType: null,
				componentDataType: null,
			} as ComponentFormat
		);
		this.uiComponentService.registerForeignKeyMapping(
			{
				databaseColumn: "FeatureWaterFeature_SK",
				databaseTable: "WaterFeatureBodyOfWater",
				applicationPropertyName: "featureWaterFeatureSK",
				databaseName: null,
				schemaName: null,
				keyType: null,
				componentDataType: null,
			} as ComponentFormat,
			{
				databaseColumn: "FeatureWaterFeature_SK",
				databaseTable: "FeatureWaterFeature",
				applicationPropertyName: "featureWaterFeatureSK",
				databaseName: null,
				schemaName: null,
				keyType: null,
				componentDataType: null,
			} as ComponentFormat
		);
	}

	private combineConfigurations([configuration, member, preferences]: [
		Configuration,
		Basics<Member>,
		any
	]): Configuration {
		const sessionBasedConfiguration =
			!!member?.boardCD && !!configuration?.override[member.boardCD.toLowerCase()]
				? configuration.override[member.boardCD.toLowerCase()]
				: {};
		const preferencesBasedConfiguration = preferences?.configuration || {};
		const arrayMerge = (destinationArray, sourceArray, _options) => sourceArray;
		return merge(
			merge(configuration, sessionBasedConfiguration, { arrayMerge }),
			preferencesBasedConfiguration
		);
	}
}
